import { useNavigate, useParams } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { Col, Row } from "react-bootstrap";

import { getdraftQuotation } from "api";
import errorToast from "helpers/errorToast";
import Button from "components/atoms/Button/Button";
import Dialog from "components/organisms/Dialog/Dialog";
import { modifyDraftData, omitScheduleStops } from "helpers/quotationFormHelper";
import Checkbox from "components/atoms/Checkbox/Checkbox";
import testIds from "constants/testIds";

interface IQuotationDuplicateDialogProps {
  isOpen: boolean;
  close: () => void;
  referenceId: string;
}

function QuotationDuplicateDialog(props: IQuotationDuplicateDialogProps) {
  const { isOpen, close, referenceId } = props;

  const methods = useForm();
  const intl = useIntl();
  const navigate = useNavigate();
  const shipmentId: any = useParams()?.id?.toUpperCase();

  const { handleSubmit } = methods;

  const handleDuplicateQuotation = (data: any) => {
    const selectedSection = Object.keys(data)
      .filter((key) => data[key])
      .join(",");
    getdraftQuotation(referenceId, shipmentId, {
      params: {
        section: selectedSection,
      },
    })
      .then((res) => {
        const existingData = { ...res.data.data };
        const updatedData = omitScheduleStops({
          ...existingData,
          schedule: {
            ...existingData.schedule,
            ...data.schedule,
          },
        });
        methods.reset(modifyDraftData(updatedData));
        navigate(`/shipment/${shipmentId}/quotation/add?active_step=service&draft_id=${res.data.reference_number}`);
        close();
      })
      .catch((error) => {
        errorToast(error);
      });
  };

  return (
    <Dialog
      size="sm"
      title={intl.formatMessage({
        id: "quote.quotation.duplicateQuotation",
        defaultMessage: "Duplicate Quotation",
      })}
      show={isOpen}
      onHide={close}
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(handleDuplicateQuotation)}>
          <div style={{ maxWidth: 340 }}>
            <p className="font-b1">
              <FormattedMessage
                id="quote.quotation.duplicateStatement"
                defaultMessage="Please select the topics to include in the duplication"
              />
            </p>
            <div className="mt-5 d-flex flex-column" style={{ gap: "20px" }}>
              <Checkbox
                providerSections
                checkboxLabel={intl.formatMessage({
                  id: "quote-request-detail.service",
                  defaultMessage: "Service",
                })}
                name="service"
              />
              <Checkbox
                providerSections
                checkboxLabel={intl.formatMessage({
                  id: "general.schedule",
                  defaultMessage: "Schedule",
                })}
                name="schedule"
              />
              <Checkbox
                providerSections
                checkboxLabel={intl.formatMessage({
                  id: "requestApproval.costs",
                  defaultMessage: "Costs",
                })}
                name="costs"
              />
              <Checkbox
                providerSections
                checkboxLabel={intl.formatMessage({
                  id: "shipment-quotes.components.quotes.serviceConfirmation",
                  defaultMessage: "Service confirmation",
                })}
                name="service_confirmation"
              />
              <Checkbox
                providerSections
                checkboxLabel={intl.formatMessage({
                  id: "quote-request-detail.additionalInformation",
                  defaultMessage: "Additional information",
                })}
                name="additional_information"
              />
            </div>
          </div>
          <Row className="mt-5">
            <Col lg={12} className="d-flex justify-content-between">
              <Button
                variant="outline-primary"
                label={intl.formatMessage({ id: "general.cancel", defaultMessage: "Cancel" })}
                size="lg"
                onClick={close}
                data-testid={testIds.SHIPMENT_DASHBOARD_QUOTATION_DUPLICATE_MODAL_CLOSE_BUTTON}
              />
              <Button
                variant="primary"
                type="submit"
                size="lg"
                label={intl.formatMessage({ id: "general.duplicate", defaultMessage: "Duplicate" })}
                data-testid={testIds.SHIPMENT_DASHBOARD_QUOTATION_DUPLICATE_MODAL_DUPLICATE_BUTTON}
              />
            </Col>
          </Row>
        </form>
      </FormProvider>
    </Dialog>
  );
}

export default QuotationDuplicateDialog;
